// BM-H82 P1-a - EPRCorrection page (post-submit correction channel).
//
// After submit, the fee earner may mint a correction session for a
// client. This page consumes the resulting tokenised URL and lets
// the client submit a correction against a submitted workbook. The
// correction is written to the working form's history[] with status
// pending_vc_review. VC authorises before amended material reaches
// the client. This is a scaffold: P1-a ships the surface and token
// layer; live VC-authorisation e2e folds into P1-b staging smoke.

import { useCallback, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ClipboardEdit, ShieldAlert } from "lucide-react";
import TokenGuard from "../components/client-forms/TokenGuard";
import { QUESTIONS } from "../lib/eprWorkbookSchema";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function InnerCorrection({ token, formId }) {
  const [reason, setReason] = useState("");
  const [selectedQ, setSelectedQ] = useState(QUESTIONS[0].id);
  const [newAnswer, setNewAnswer] = useState("");
  const [status, setStatus] = useState("idle");
  const [detail, setDetail] = useState(null);

  const submit = useCallback(async () => {
    if (!reason.trim()) {
      setStatus("error");
      setDetail("Please tell your solicitor why you want to correct this answer.");
      return;
    }
    setStatus("submitting");
    try {
      const resp = await fetch(`${API_BASE}/api/epr/corrections/${formId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Correction-Token": token },
        body: JSON.stringify({
          reason: reason.trim(),
          answer_patches: {
            [selectedQ]: { answer: newAnswer, confidence: "unsure", client_note: "" },
          },
        }),
      });
      if (!resp.ok) throw new Error(`submit failed: ${resp.status}`);
      const data = await resp.json();
      setStatus("submitted");
      setDetail(data);
    } catch (e) {
      setStatus("error");
      setDetail(e.message || "Unable to submit correction");
    }
  }, [formId, token, reason, selectedQ, newAnswer]);

  if (status === "submitted") {
    return (
      <div
        data-testid="correction-thanks"
        style={{ maxWidth: 640, margin: "80px auto", padding: 32, background: "#fff", borderRadius: 10, fontFamily: "Georgia, serif" }}
      >
        <ShieldAlert size={32} color="#4338ca" />
        <h1 style={{ fontSize: 22 }}>Correction sent for supervisor review</h1>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: "#334155" }}>
          Thank you. Your correction is queued for our supervising principal (VC) to authorise. Amended material only issues to you once VC has authorised the change. Your original submission record remains on file exactly as it was.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="correction-form"
      style={{ maxWidth: 720, margin: "48px auto", padding: 32, background: "#fff", borderRadius: 10, fontFamily: "Georgia, serif" }}
    >
      <ClipboardEdit size={24} color="#4338ca" />
      <h1 style={{ fontSize: 22 }}>Send a correction to your solicitor</h1>
      <p style={{ fontSize: 14, lineHeight: 1.6, color: "#334155" }}>
        Corrections are reviewed by our supervising principal (VC) before any amended material issues to you. Your original submission record remains immutable.
      </p>
      <label htmlFor="correction-question" style={{ display: "block", marginTop: 16, fontWeight: 600 }}>
        Which question do you want to correct?
      </label>
      <select
        id="correction-question"
        data-testid="correction-question-select"
        value={selectedQ}
        onChange={(e) => setSelectedQ(e.target.value)}
        style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 6 }}
      >
        {QUESTIONS.map((q) => (
          <option key={q.id} value={q.id}>{q.id.toUpperCase()} - {q.prompt.slice(0, 60)}...</option>
        ))}
      </select>

      <label htmlFor="correction-new-answer" style={{ display: "block", marginTop: 16, fontWeight: 600 }}>
        What should the answer say now?
      </label>
      <textarea
        id="correction-new-answer"
        data-testid="correction-new-answer"
        value={newAnswer}
        onChange={(e) => setNewAnswer(e.target.value)}
        rows={4}
        style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 6, fontFamily: "inherit" }}
      />

      <label htmlFor="correction-reason" style={{ display: "block", marginTop: 16, fontWeight: 600 }}>
        Why does this need correcting?
      </label>
      <textarea
        id="correction-reason"
        data-testid="correction-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #cbd5e1", marginTop: 6, fontFamily: "inherit" }}
      />

      {status === "error" && (
        <div data-testid="correction-error" style={{ marginTop: 16, color: "#b91c1c", fontSize: 13 }}>
          {typeof detail === "string" ? detail : "Something went wrong. Please try again or email your solicitor."}
        </div>
      )}

      <button
        type="button"
        data-testid="correction-submit-btn"
        onClick={submit}
        disabled={status === "submitting"}
        style={{ marginTop: 20, padding: "10px 18px", background: "#4338ca", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700 }}
      >
        {status === "submitting" ? "Sending..." : "Send correction for review"}
      </button>
    </div>
  );
}

export default function EPRCorrection() {
  const { form_instance_id: formId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  return (
    <TokenGuard token={token}>
      <InnerCorrection token={token} formId={formId} />
    </TokenGuard>
  );
}
